import React, { useState } from "react";
import { toast } from "sonner";
import { Copy, EnvelopeSimple } from "@phosphor-icons/react";
import { btnGhost } from "../ui";

const PERMS = "API permissions > Add a permission > Microsoft Graph > Delegated permissions: Mail.ReadWrite, Mail.Send, User.Read and offline_access > Add permissions, then click \"Grant admin consent\"";
const SETTING = "Authentication > Settings (\"Advanced settings\" on older screens) > \"Allow public client flows\" > Yes > Save";

const steps = (links) => links
  ? `For our app registration (Application ID ${links.app_id}):\n${links.overview}\n\n1. ${SETTING}.\n2. ${PERMS}.\n\nIf Microsoft sent you an approval request for this app, approving it covers step 2.`
  : `1. App registrations > New registration: name "OTM Tech AI - Warranty drafts", accounts in this organisational directory only, no redirect URI needed.\n2. ${SETTING}.\n3. ${PERMS}.\n\nThen please send me the Directory (tenant) ID and the Application (client) ID. No client secret is needed.`;

const body = (mailbox, links) => `Hi,

Our warranty app (OTM Tech AI) creates warranty emails in the ${mailbox} mailbox and sends them from there: approval confirmations to our dealers, and parts requests to our factory.

We sign in to Microsoft as ${mailbox} inside the app, so it can only reach that one mailbox. Could you please do the following in Microsoft Entra (entra.microsoft.com):

${steps(links)}

After that we sign in once from the app.

Thanks!`;

export function ItAdminRequest({ mailbox, links, highlight }) {
  const [to, setTo] = useState("");
  const subject = links ? "Please approve OTM Tech AI for the warranty@ mailbox" : "Microsoft 365 app registration request: OTM Tech AI warranty email drafts";
  const text = body(mailbox, links);
  const copy = () => navigator.clipboard.writeText(`Subject: ${subject}\n\n${text}`).then(() => toast.success("Request copied. Paste it into an email to your IT admin")).catch(() => toast.error("Couldn't copy"));
  const mailto = `mailto:${to.trim()}?${new URLSearchParams({ subject, body: text }).toString().replace(/\+/g, "%20")}`;
  return (
    <div className={`border rounded-xl p-4 space-y-3 ${highlight ? "border-amber-500/40 bg-amber-500/[0.04]" : "border-neutral-800 bg-black/30"}`} data-testid="wi-it-request">
      <div className="text-sm text-neutral-200 font-semibold">{links ? "Email your IT admin" : "Step 1 · Ask your Microsoft 365 admin"}</div>
      <p className="text-xs text-neutral-400">{links
        ? "A ready-made message asking IT to approve the app (including permission to send from warranty@) and switch on the one setting it needs."
        : "Microsoft needs the app registered once in your company account. This ready-made request tells your IT admin exactly what to set up (only the warranty@ mailbox, no secret needed)."}</p>
      <details className="text-xs text-neutral-400"><summary className="cursor-pointer text-[#73bf44]" data-testid="wi-it-request-toggle">Read the message</summary>
        <pre className="whitespace-pre-wrap font-sans mt-2 bg-black/40 border border-neutral-800 rounded-lg p-3 text-neutral-300 break-words" data-testid="wi-it-request-text">{text}</pre></details>
      <input type="email" value={to} onChange={(e) => setTo(e.target.value)} placeholder="IT admin's email (optional)" data-testid="wi-it-request-to"
        className="w-full bg-black/50 border border-neutral-800 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-[#73bf44] transition-colors" />
      <div className="flex flex-wrap gap-2">
        <a href={mailto} className={btnGhost} data-testid="wi-it-request-email-btn"><EnvelopeSimple size={15} /> Email IT</a>
        <button className={btnGhost} onClick={copy} data-testid="wi-it-request-copy-btn"><Copy size={15} /> Copy message</button>
      </div>
    </div>
  );
}
